import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Trophy, Calendar, ImageIcon, ChevronRight } from "lucide-react";
import { PageHero, PageBody, Lightbox, formatDate, slugify, FONT } from "./_shared";
import { useBul } from "./BulLayout";

export default function CampusEngagements() {
  const data = useBul();
  const { typeSlug } = useParams();
  const navigate = useNavigate();
  const allEvents = data.events || [];
  const list = useMemo(() => {
    if (!typeSlug) return allEvents;
    return allEvents.filter((e) => slugify(e.event_type || "") === typeSlug.toLowerCase());
  }, [allEvents, typeSlug]);
  const activeTypeLabel = typeSlug ? list[0]?.event_type || typeSlug : "";

  const [activeId, setActiveId] = useState(list[0]?.id || null);
  const [lbIdx, setLbIdx] = useState(-1);

  useEffect(() => {
    setActiveId(list[0]?.id || null);
    setLbIdx(-1);
  }, [typeSlug, list]);

  const active = list.find((e) => e.id === activeId) || null;

  return (
    <div data-testid="bul-engagements">
      <PageHero
        eyebrow={typeSlug ? `Stories · ${activeTypeLabel}` : "Stories"}
        title="Campus Life"
      />
      <PageBody>
        {typeSlug && (
          <div className="mb-6 flex items-center justify-between bg-[#F8F5EE] border border-[#FDBB30] px-5 py-3 text-sm" data-testid="bul-engagements-filter-bar">
            <span className="text-[#2A2A2B]">
              Showing <span className="font-semibold text-[#0F0F10]">{activeTypeLabel}</span> ({list.length})
            </span>
            <a
              href="../campus-engagements"
              onClick={(e) => { e.preventDefault(); navigate("../campus-engagements"); }}
              className="text-[11px] tracking-[0.22em] uppercase text-[#0F0F10] hover:text-[#E19E14] font-semibold border-b-2 border-[#FDBB30] pb-0.5"
              data-testid="bul-engagements-clear-filter"
            >
              Show All
            </a>
          </div>
        )}
        {list.length === 0 ? (
          <div className="text-center bg-white p-16 border border-[#D9D2C1] text-[#726C61]">
            {typeSlug
              ? `No ${activeTypeLabel} stories published yet.`
              : "Campus moments will appear here once the institute publishes them."}
          </div>
        ) : (
          <div className="grid md:grid-cols-[300px_1fr] gap-6">
            <aside
              className="bg-white border border-[#D9D2C1] md:sticky md:top-24 self-start"
              data-testid="bul-engagements-list"
            >
              <div className="px-5 py-4 border-b border-[#D9D2C1]">
                <div className="text-[10px] tracking-[0.35em] uppercase text-[#FDBB30] font-semibold">Browse</div>
                <div className="mt-1 text-lg text-[#0F0F10]" style={{ fontFamily: FONT.serif, fontWeight: 700 }}>
                  {typeSlug ? `${activeTypeLabel}` : "All Stories"} ({list.length})
                </div>
              </div>
              <ul className="max-h-[70vh] overflow-y-auto">
                {list.map((e) => {
                  const isActive = e.id === active?.id;
                  const isAch = (e.event_type || "").toLowerCase().includes("achievement");
                  return (
                    <li key={e.id}>
                      <button
                        type="button"
                        onClick={() => { setActiveId(e.id); setLbIdx(-1); }}
                        data-testid={`bul-event-tab-${e.id}`}
                        className={`w-full text-left px-5 py-3 flex items-start gap-3 border-l-4 transition-colors ${
                          isActive ? "bg-[#F8F5EE] border-[#FDBB30]" : "border-transparent hover:bg-[#F8F5EE]"
                        }`}
                      >
                        <span className={`w-8 h-8 flex items-center justify-center shrink-0 ${isAch ? "bg-[#FDBB30] text-[#0F0F10]" : "bg-[#0F0F10] text-[#FDBB30]"}`}>
                          <Trophy size={14} strokeWidth={1.6} />
                        </span>
                        <span className="flex-1 min-w-0">
                          {e.event_type && (
                            <span
                              className={`inline-block text-[9px] tracking-[0.25em] uppercase font-bold px-1.5 py-0.5 mb-1 ${
                                isAch ? "bg-[#FDBB30] text-[#0F0F10]" : "bg-[#0F0F10] text-white"
                              }`}
                              data-testid={`bul-event-type-${e.id}`}
                            >
                              {e.event_type}
                            </span>
                          )}
                          <span className={`block text-sm leading-snug ${isActive ? "text-[#0F0F10] font-semibold" : "text-[#2A2A2B]"}`}>
                            {e.title}
                          </span>
                          <span className="block text-[11px] text-[#726C61] mt-0.5">
                            {formatDate(e.event_date)}
                          </span>
                        </span>
                        <ChevronRight size={14} strokeWidth={1.6} className={`mt-1 ${isActive ? "text-[#FDBB30]" : "text-[#D9D2C1]"}`} />
                      </button>
                    </li>
                  );
                })}
              </ul>
            </aside>

            {active && (
              <article
                key={active.id}
                data-testid={`bul-event-${active.id}`}
                className="bg-white border border-[#D9D2C1]"
              >
                <div className="px-8 pt-8 flex flex-wrap items-center gap-3">
                  <span
                    className={`text-[10px] tracking-[0.25em] uppercase px-3 py-1 font-bold ${
                      (active.event_type || "").toLowerCase().includes("achievement")
                        ? "bg-[#FDBB30] text-[#0F0F10]"
                        : "bg-[#0F0F10] text-white"
                    }`}
                  >
                    {active.event_type}
                  </span>
                  <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-[0.25em] text-[#726C61] font-semibold">
                    <Calendar size={12} strokeWidth={1.6} />
                    {formatDate(active.event_date)}
                  </div>
                </div>

                <div className="px-8 pt-4 pb-6">
                  <h2 className="text-3xl md:text-4xl text-[#0F0F10] leading-tight" style={{ fontFamily: FONT.serif, fontWeight: 700 }}>
                    {active.title}
                  </h2>
                  {active.description && (
                    <p className="mt-5 text-[#2A2A2B] leading-[1.9] whitespace-pre-line">
                      {active.description}
                    </p>
                  )}
                </div>

                {(active.photos || []).length > 0 && (
                  <div className="px-8 pb-8">
                    <div className="text-[10px] tracking-[0.3em] uppercase text-[#FDBB30] font-semibold mb-3 flex items-center gap-1.5">
                      <ImageIcon size={11} strokeWidth={1.6} />
                      Photos ({active.photos.length})
                    </div>
                    <div className="flex gap-3 overflow-x-auto pb-1 -mx-1 px-1 snap-x snap-mandatory sm:flex-wrap sm:overflow-visible">
                      {active.photos.map((p, i) => (
                        <button
                          key={p.url + i}
                          type="button"
                          onClick={() => setLbIdx(i)}
                          className="shrink-0 w-40 h-28 sm:w-44 sm:h-32 overflow-hidden ring-1 ring-[#D9D2C1] hover:ring-[#FDBB30] hover:ring-2 transition-all snap-start group"
                          aria-label={`Photo ${i + 1} of ${active.photos.length}`}
                        >
                          <img
                            src={p.url}
                            alt={`${active.title} — photo ${i + 1}`}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                            loading="lazy"
                          />
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </article>
            )}
          </div>
        )}
      </PageBody>
      {active && lbIdx >= 0 && (active.photos || []).length > 0 && (
        <Lightbox
          photos={(active.photos || []).map((p) => ({ image_url: p.url, caption: active.title }))}
          idx={lbIdx}
          onIdx={setLbIdx}
          onClose={() => setLbIdx(-1)}
        />
      )}
    </div>
  );
}
